import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import tenantStatusService, { TenantStatusResponse } from '@/services/tenant-status.service';

const BRANDING_CACHE_KEY = 'tenant_branding';

interface CachedBranding {
  company_name?: string;
  logo_url?: string;
  small_logo_url?: string;
}

function readCachedBranding(): CachedBranding {
  try {
    const raw = localStorage.getItem(BRANDING_CACHE_KEY);
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

export default function useTenantBranding() {
  const cached = useMemo(() => readCachedBranding(), []);

  const { data: tenantStatus } = useQuery<TenantStatusResponse>({
    queryKey: ['tenantStatus'],
    queryFn: () => tenantStatusService.checkStatus(),
    staleTime: 5 * 60 * 1000,
  });

  const companyName = tenantStatus?.company_name?.trim() || cached.company_name?.trim() || 'Skillzen';
  const smallLogoUrl = tenantStatus?.small_logo_url || cached.small_logo_url;
  const logoUrl = tenantStatus?.logo_url || cached.logo_url;

  // Persist branding to cache when tenantStatus responds
  useEffect(() => {
    if (tenantStatus?.company_name || tenantStatus?.small_logo_url || tenantStatus?.logo_url) {
      try {
        localStorage.setItem(
          BRANDING_CACHE_KEY,
          JSON.stringify({
            company_name: tenantStatus.company_name,
            logo_url: tenantStatus.logo_url,
            small_logo_url: tenantStatus.small_logo_url,
          })
        );
      } catch {
        // Ignore storage write issues
      }
    }
  }, [tenantStatus]);

  // Dynamic Favicon (uses small_logo_url / collapsed icon from Business Settings)
  useEffect(() => {
    let link: HTMLLinkElement | null = document.querySelector("link[rel*='icon']");
    if (!link) {
      link = document.createElement('link');
      link.rel = 'icon';
      document.head.appendChild(link);
    }

    if (smallLogoUrl && smallLogoUrl.trim() !== '') {
      link.href = smallLogoUrl;
    } else {
      link.href = '/favicon.svg';
    }
  }, [smallLogoUrl]);

  // Runtime document.title: preserves the exact LMS title format, replacing "Skillzen Admin" with company_name
  useEffect(() => {
    const titlePrefix = companyName !== 'Skillzen' ? companyName : 'Skillzen Admin';
    document.title = `${titlePrefix} - Learning Management System`;
  }, [companyName]);

  return {
    companyName,
    logoUrl,
    smallLogoUrl,
  };
}
